import { useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Auto-extend session: silently refresh JWT every ~25 min while
 * (a) the user has the preference enabled, (b) the tab is visible/active.
 *
 * The user's preference is loaded from /users/me/preferences once authenticated,
 * and re-fetched on the `outplay:prefs-changed` event so toggling on Profile
 * picks up immediately.
 */
const HEARTBEAT_INTERVAL_MS = 25 * 60 * 1000; // 25 minutes
const ACTIVITY_WINDOW_MS = 30 * 60 * 1000;     // require activity in last 30 min

export function useSessionHeartbeat() {
  const { user, setToken } = useAuth();
  const intervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const enabledRef = useRef(false);

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;

    const fetchPref = async () => {
      try {
        const r = await api.get('/users/me/preferences');
        if (!cancelled) enabledRef.current = !!r.data.auto_extend_session;
      } catch (e) {
        if (!cancelled) enabledRef.current = false;
      }
    };
    fetchPref();

    const onPrefChanged = () => fetchPref();
    window.addEventListener('outplay:prefs-changed', onPrefChanged);

    // Track user activity (mouse, keyboard, scroll) so we don't refresh idle tabs
    const markActive = () => { lastActivityRef.current = Date.now(); };
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(ev =>
      window.addEventListener(ev, markActive, { passive: true })
    );

    const tick = async () => {
      if (!enabledRef.current) return;
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor > ACTIVITY_WINDOW_MS) return; // silently no-op
      if (document.hidden) return;
      try {
        const r = await api.post('/auth/refresh');
        if (r.data?.access_token && setToken) {
          setToken(r.data.access_token);
          localStorage.setItem('outplay_token', r.data.access_token);
        }
      } catch (e) {
        // Silent fail — global 401 handler will redirect if session truly expired
      }
    };

    intervalRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('outplay:prefs-changed', onPrefChanged);
      ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(ev =>
        window.removeEventListener(ev, markActive)
      );
    };
  }, [user, setToken]);
}
